import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, Mail, Clock, ShieldCheck, Send, CheckCircle2 } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/motion/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { BRAND } from "@/data/content";
import { toast } from "sonner";

const INTERESTS = [
  "Home Guardian (In-Home)",
  "GoMobile GPS (Mobile)",
  "FallGuard Pro (Fall Detection)",
  "LifeWatch Smart (Smartwatch)",
  "Basic Plan",
  "Preferred Plan",
  "Premium Plan",
  "Not sure yet",
];

const inputClass =
  "h-14 text-base rounded-xl border-brand-line bg-white focus-visible:ring-brand-red/30 focus-visible:border-brand-navy";

export default function Contact() {
  const [params] = useSearchParams();
  const planParam = params.get("plan");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    zip_code: "",
    interest: planParam ? `${planParam} Plan` : "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, kind: "quote" });
      setDone(true);
      toast.success("Request received!", { description: "A specialist will reach out shortly." });
    } catch {
      toast.error("Something went wrong. Please call us at " + BRAND.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Get In Touch"
        title={<>Get your free <span className="text-brand-red">60-second quote.</span></>}
        sub="Tell us a little about your needs and a US-based specialist will help you find the right device — no pressure, no obligation."
      />

      <section className="py-16 lg:py-24 bg-brand-bg">
        <div className="container-x grid lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-14 items-start">
          {/* info */}
          <Reveal>
            <div className="rounded-3xl bg-brand-navy text-white p-8 lg:p-10">
              <h2 className="font-display font-bold text-3xl">Talk to a specialist</h2>
              <p className="mt-3 text-white/70 leading-relaxed">
                Prefer to talk? Our team is standing by 24/7. There's never any pressure — just
                honest help.
              </p>
              <div className="mt-8 space-y-5">
                <a href={BRAND.phoneHref} className="flex items-center gap-4 group" data-testid="contact-phone">
                  <span className="grid place-items-center w-12 h-12 rounded-full bg-brand-red shrink-0">
                    <Phone className="w-5 h-5" strokeWidth={2.6} />
                  </span>
                  <span>
                    <span className="block text-sm text-white/60">Call us anytime</span>
                    <span className="font-display font-bold text-xl group-hover:text-brand-red transition-colors">
                      {BRAND.phone}
                    </span>
                  </span>
                </a>
                <a href={`mailto:${BRAND.email}`} className="flex items-center gap-4 group">
                  <span className="grid place-items-center w-12 h-12 rounded-full bg-white/10 shrink-0">
                    <Mail className="w-5 h-5" />
                  </span>
                  <span>
                    <span className="block text-sm text-white/60">Email us</span>
                    <span className="font-semibold text-lg group-hover:text-brand-red transition-colors">
                      {BRAND.email}
                    </span>
                  </span>
                </a>
                <div className="flex items-center gap-4">
                  <span className="grid place-items-center w-12 h-12 rounded-full bg-white/10 shrink-0">
                    <Clock className="w-5 h-5" />
                  </span>
                  <span>
                    <span className="block text-sm text-white/60">Hours</span>
                    <span className="font-semibold text-lg">24 / 7 / 365</span>
                  </span>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-2 text-white/70">
                <ShieldCheck className="w-5 h-5 text-brand-red" />
                <span className="text-sm font-semibold">No obligation · No contract · Cancel any time</span>
              </div>
            </div>
          </Reveal>

          {/* form */}
          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-white border border-brand-line shadow-card p-8 lg:p-10">
              {done ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <span className="grid place-items-center w-20 h-20 rounded-full bg-brand-red/10 mx-auto">
                    <CheckCircle2 className="w-11 h-11 text-brand-red" strokeWidth={2} />
                  </span>
                  <h3 className="mt-6 font-display font-bold text-3xl text-brand-navy">Thank you!</h3>
                  <p className="mt-3 text-lg text-brand-ink2 max-w-md mx-auto">
                    Your request is in. A LifeButton specialist will reach out shortly. Need help
                    right now? Call{" "}
                    <a href={BRAND.phoneHref} className="font-bold text-brand-red">
                      {BRAND.phone}
                    </a>
                    .
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="quote-form" className="space-y-5">
                  <h3 className="font-display font-bold text-2xl text-brand-navy">
                    Request your free quote
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-brand-navy font-semibold">Full name *</Label>
                      <Input id="name" data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className={inputClass} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-brand-navy font-semibold">Phone *</Label>
                      <Input id="phone" data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" className={inputClass} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-brand-navy font-semibold">Email *</Label>
                      <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputClass} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zip" className="text-brand-navy font-semibold">ZIP code</Label>
                      <Input id="zip" data-testid="input-zip" value={form.zip_code} onChange={set("zip_code")} placeholder="90210" className={inputClass} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="interest" className="text-brand-navy font-semibold">I'm interested in</Label>
                    <select
                      id="interest"
                      data-testid="select-interest"
                      value={form.interest}
                      onChange={set("interest")}
                      className="w-full h-14 px-4 text-base rounded-xl border border-brand-line bg-white text-brand-navy focus:outline-none focus:ring-4 focus:ring-brand-red/20 focus:border-brand-navy"
                    >
                      <option value="">Select an option</option>
                      {INTERESTS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-brand-navy font-semibold">How can we help?</Label>
                    <Textarea id="message" data-testid="input-message" value={form.message} onChange={set("message")} placeholder="Tell us who this is for and what matters most to you..." className="min-h-[120px] text-base rounded-xl border-brand-line focus-visible:ring-brand-red/30 focus-visible:border-brand-navy" />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-quote-button"
                    className="btn-focus w-full inline-flex items-center justify-center gap-2.5 h-15 min-h-[60px] rounded-full bg-brand-red text-white font-bold text-lg hover:bg-brand-red-600 transition-colors shadow-red disabled:opacity-60"
                  >
                    {loading ? "Sending..." : <>Get my free quote <Send className="w-5 h-5" strokeWidth={2.4} /></>}
                  </button>
                  <p className="text-center text-sm text-brand-ink2">
                    By submitting, you agree to be contacted about LifeButton services.
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
